import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setUnauthorizedHandler } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.me();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = async (email, password) => {
    try {
      const user = await api.login({ email, password });
      setUser(user);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      return { ok: true, user };
    } catch (e) {
      toast.error(e.message);
      return { ok: false, error: e.message };
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      const user = await api.register({ name, email, password, phone });
      setUser(user);
      toast.success("Account created — welcome to YourPlants!");
      return { ok: true, user };
    } catch (e) {
      toast.error(e.message);
      return { ok: false, error: e.message };
    }
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    toast.success("Signed out");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
